<template>
    <div class="ge-block">
        <div class="ge-header">
            <span class="ge-title">{{ group.name }}</span>
            <span class="ge-meta">{{ $tc('company.members_count', memberCount, { n: memberCount }) }}</span>
        </div>

        <skeleton-loader v-if="loading" type="team-bars" />

        <template v-else>
            <rank-list :title="$t('company.attendance_last_days', { n: attendanceDays })" :items="rankItems" />

            <div class="adherence-row">
                <span class="adherence-num">{{ adherenceLabel }}</span>
                <div class="adherence-track">
                    <div class="adherence-fill" :style="{ width: adherenceWidth }" />
                </div>
                <span class="adherence-label">{{ $t('company.completed_week_routine') }}</span>
            </div>

            <div class="section-wellness">
                <div class="wellness-header">
                    <div class="dash-title">{{ $t('company.group_wellness') }}</div>
                    <div class="dash-meta">
                        {{ $t('dashboard.today') }} ·
                        {{ $t('dashboard.responses_of', { r: entries.length, t: memberCount }) }}
                    </div>
                </div>
                <div v-if="entries.length" class="wellness-circles">
                    <wellness-ring v-for="ring in rings" :key="ring.key" :value="ring.value" :icon="ring.icon"
                        :label="$t(ring.labelKey)" />
                </div>
                <div v-else class="empty-state">{{ $t('company.group_without_wellness') }}</div>
            </div>
        </template>
    </div>
</template>

<script>
import { UserIndex } from "@/utils/Algolia";
import { fetchStudentsAdvances } from "@/utils/Statistics";
import { studentWeekStart } from "@/utils/Routine";
import { entriesOfDay, buildRingValues } from "@/utils/Wellness";
import RankList from "@/components/common/RankList";
import WellnessRing from "@/components/common/WellnessRing";
import SkeletonLoader from "@/components/common/SkeletonLoader";

const ATTENDANCE_DAYS = 30

export default {
    name: "CoachGroupDetail",
    components: { RankList, WellnessRing, SkeletonLoader },
    props: {
        group: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            roster: [],
            advances: [],
            loading: false,
            attendanceDays: ATTENDANCE_DAYS,
        }
    },
    computed: {
        memberCount(vm = this) {
            return vm.roster.length || (vm.group.students || []).length
        },
        today(vm = this) {
            return vm.$moment().format('DD-MM-YYYY')
        },
        entries(vm = this) {
            return entriesOfDay(vm.roster, vm.today)
        },
        rings(vm = this) {
            return buildRingValues(vm.entries)
        },
        training(vm = this) {
            return vm.advances.filter(item => item.type !== 'competence')
        },
        rankItems(vm = this) {
            const byStudent = {}
            vm.training.forEach(item => {
                const id = item.studentId
                if (!id) return
                if (!byStudent[id]) byStudent[id] = { assigned: 0, completed: 0 }
                byStudent[id].assigned += 1
                if (item.advance?.post) byStudent[id].completed += 1
            })

            return vm.roster
                .map(student => {
                    const stats = byStudent[student.id] || { assigned: 0, completed: 0 }
                    return {
                        id: student.id,
                        name: student.shortName,
                        value: stats.completed,
                        max: stats.assigned,
                        label: `${stats.completed}/${stats.assigned}`,
                        ratio: stats.assigned > 0 ? stats.completed / stats.assigned : 0,
                    }
                })
                .sort((a, b) => b.ratio - a.ratio)
        },
        weekAdherence(vm = this) {
            const start = studentWeekStart().format('YYYY-MM-DD')
            const week = vm.training.filter(item => (item.rawDate?.full || '') >= start)
            if (week.length === 0) return null
            const completed = week.filter(item => item.advance?.post).length
            return Math.round((completed / week.length) * 100)
        },
        adherenceLabel(vm = this) {
            return vm.weekAdherence === null ? '—' : `${vm.weekAdherence}%`
        },
        adherenceWidth(vm = this) {
            return `${vm.weekAdherence || 0}%`
        },
    },
    mounted() {
        this.loadAll()
    },
    methods: {
        async loadAll() {
            const vm = this
            if (!vm.group?.id) return
            vm.loading = true
            try {
                await Promise.all([vm.loadRoster(), vm.loadAdvances()])
            } finally {
                vm.loading = false
            }
        },
        async loadRoster() {
            const vm = this
            try {
                const { hits } = await UserIndex.search('', {
                    filters: `group: ${vm.group.id} AND studentSubscription.paused != 1`,
                    hitsPerPage: 500,
                })
                vm.roster = hits.map(hit => ({
                    id: hit.objectID,
                    name: hit.name || hit.email || vm._noAvailableName,
                    shortName: vm.shortName(hit.name || hit.email || ''),
                    lastWellness: hit.lastWellness || [],
                }))
            } catch (error) {
                console.log('loadRoster error', error)
            }
        },
        async loadAdvances() {
            const vm = this
            const end = vm.$moment().format('YYYY-MM-DD')
            const start = vm.$moment().subtract(ATTENDANCE_DAYS - 1, 'days').format('YYYY-MM-DD')
            try {
                vm.advances = await fetchStudentsAdvances({ groupId: vm.group.id, start, end })
            } catch (error) {
                console.log('loadAdvances error', error)
            }
        },
        shortName(name) {
            const parts = String(name || '').trim().split(/\s+/)
            if (parts.length < 2) return parts[0] || ''
            return `${parts[0]} ${parts[1].charAt(0).toUpperCase()}.`
        },
    },
    watch: {
        'group.id'() {
            this.loadAll()
        },
    },
}
</script>

<style scoped>
.ge-block {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.ge-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
}

.ge-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}

.ge-meta {
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.adherence-row {
    display: flex;
    align-items: center;
    gap: 12px;
}

.adherence-num {
    font-size: 22px;
    font-weight: 800;
    color: var(--v-secondary-base);
    letter-spacing: -0.02em;
}

.adherence-track {
    flex: 1;
    height: 8px;
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 4px;
    overflow: hidden;
}

.adherence-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--v-accent-base), var(--v-secondary-base));
    border-radius: 4px;
}

.adherence-label {
    font-size: 11px;
    color: var(--v-txtSecondary-base);
}

.section-wellness {
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
    border-radius: 12px;
    padding: 14px 16px;
}

.wellness-header {
    margin-bottom: 14px;
}

.dash-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--v-txtPrimary-base);
}

.dash-meta {
    font-size: 11px;
    color: var(--v-txtSecondary-base);
    margin-top: 1px;
}

.wellness-circles {
    display: flex;
    justify-content: space-around;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.empty-state {
    font-size: 12px;
    color: var(--v-txtMuted-base);
    font-style: italic;
    text-align: center;
    padding: 12px 0;
}

@media (max-width: 600px) {
    .adherence-label {
        display: none;
    }
}
</style>
